import { client } from "@/modules/integrations/documents/client";
import type { GeneralDocument } from "@/providers/smarta-documents/documents-api-client";

import { contentTypeOf, isAcceptedFile } from "./files";
import {
  DeleteGeneralDocumentBody,
  DeletionReason,
  DocumentType,
  LeadDocument,
} from "./types";

/*
 * A lead's onboarding documents are general documents in smarta-documents,
 * reached through the kontax-backend documents gateway. The documents
 * partition is keyed by the lead's public id: smarta-documents calls it
 * `customerId`, and converting a lead is designed to keep that id.
 */
const UPLOADS = "/api/customers/{customerId}/uploads";
const GENERAL_DOCUMENTS = "/api/customers/{customerId}/general-documents";
const PAGE_SIZE = 100;

const listGeneralDocumentsPage = async (
  leadId: string,
  limit: number,
  offset: number
) => {
  const { data, error } = await client.GET(GENERAL_DOCUMENTS, {
    params: {
      path: { customerId: leadId },
      query: { limit, offset },
    },
  });
  if (error || !data) {
    throw error || new Error("Malformed list-general-documents response");
  }
  return data;
};

const toLeadDocuments = (documents: GeneralDocument[]): LeadDocument[] =>
  documents.map((document) => ({
    documentId: document.id,
    documentType: document.documentType,
    fileName: document.fileName,
    contentType: document.contentType,
    uploadedAt: document.createdAt,
    uploadedByOperatorId: document.uploadedByOperatorId,
  }));

/** Every general document of a lead, newest first, across all pages. */
export const listLeadDocuments = async (
  leadId: string
): Promise<LeadDocument[]> => {
  const first = await listGeneralDocumentsPage(leadId, PAGE_SIZE, 0);
  const documents = toLeadDocuments(first.data);
  for (
    let offset = PAGE_SIZE;
    offset < first.pagination.total;
    offset += PAGE_SIZE
  ) {
    const next = await listGeneralDocumentsPage(leadId, PAGE_SIZE, offset);
    documents.push(...toLeadDocuments(next.data));
  }
  return documents;
};

/**
 * How many files a lead has. The list has no count endpoint, so this asks for
 * one row and reads the pagination total.
 */
export const countLeadDocuments = async (leadId: string): Promise<number> =>
  (await listGeneralDocumentsPage(leadId, 1, 0)).pagination.total;

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });

/**
 * Upload one file into a document field. The uploader is not sent: the
 * gateway records the operator from the session.
 */
export const uploadLeadDocument = async (
  leadId: string,
  documentType: DocumentType,
  file: File
): Promise<void> => {
  const contentType = contentTypeOf(file);
  if (!contentType || !isAcceptedFile(documentType, file)) {
    throw new Error(`Unsupported file for ${documentType}`);
  }
  const { error } = await client.POST(UPLOADS, {
    params: { path: { customerId: leadId } },
    body: {
      fileName: file.name,
      contentType,
      contentBase64: await fileToBase64(file),
      documentType,
    },
  });
  if (error) throw error;
};

/**
 * Delete one file of a lead, for the given reason. The operator is not sent:
 * the gateway records it from the session, so the body is smarta-documents'
 * own body without `deletedByOperatorId`. A 404 (unknown or already deleted)
 * comes back without a body, so the status is checked, not only `error`.
 */
export const deleteLeadDocument = async (
  leadId: string,
  documentId: string,
  deletionReason: DeletionReason
): Promise<void> => {
  const { error, response } = await client.DELETE(
    "/api/customers/{customerId}/general-documents/{documentId}",
    {
      params: { path: { customerId: leadId, documentId } },
      body: { deletionReason } as DeleteGeneralDocumentBody,
    }
  );
  if (error || !response.ok) {
    throw error || new Error(`Delete failed with status ${response.status}`);
  }
};

/** A short-lived signed URL to the file of one general document. */
export const getLeadDocumentUrl = async (
  leadId: string,
  documentId: string
): Promise<string> => {
  const { data, error } = await client.GET(
    "/api/customers/{customerId}/general-documents/{documentId}/file-url",
    { params: { path: { customerId: leadId, documentId } } }
  );
  if (error || !data?.data?.url) {
    throw error || new Error("Malformed file-url response");
  }
  return data.data.url;
};
